import React, { useState, useEffect } from 'react';
import './App.css';
import axios from 'axios';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import ResearchLibrary from './components/ResearchLibrary';
import AgentSwarmProgress from './components/AgentSwarmProgress';
import AnalysisProvenance from './components/AnalysisProvenance';
import AgenticResearch from './components/AgenticResearch';
import ConfidenceAudit from './components/ConfidenceAudit';
import PdfModeView from './components/PdfModeView';
import Wizard from './components/wizard/Wizard';
import AnalysisResults from './components/results/AnalysisResults';
import HistoryList from './components/results/HistoryList';
import logger from './utils/logger';
import {
  BACKEND_URL,
  API,
  renderBoldText,
  getCompanyIcon,
  getMediaIcon,
  formatTimeRemaining,
  getStepIcon,
} from './lib/helpers';
// Import React Icons for professional icons
import { 
  FaGlobe, 
  FaUsers, 
  FaBrain, 
  FaShoppingCart, 
  FaBuilding,
  FaApple,
  FaGoogle,
  FaMicrosoft,
  FaAmazon,
  FaFacebook,
  FaAirbnb,
  FaUber,
  FaSpotify,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaNike,
  FaPaypal,
  FaStripe,
  FaSalesforce,
  FaSlack,
  FaDropbox,
  FaShopify,
  FaHotel,
  FaPlane,
  FaCar,
  FaMapMarkerAlt,
  FaTicketAlt,
  FaUmbrellaBeach,
  FaMountain,
  FaCity,
  FaGamepad,
  FaFilm,
  FaMusic,
  FaCamera,
  FaWifi,
  FaCoffee,
  FaPizzaSlice,
  FaHamburger,
  FaBeer,
  FaWineGlass
} from 'react-icons/fa';
import { 
  SiAdobe,
  SiAsana,
  SiTrello,
  SiNotion,
  SiFigma,
  SiCanva,
  SiVisa,
  SiMastercard
} from 'react-icons/si';

// Import Heroicons for professional icons
import { 
  ChartBarIcon,
  TvIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrophyIcon,
  StarIcon
} from '@heroicons/react/24/outline';

// Import Enhanced Logo System (used by sub-components and analyses)
import { getEnhancedCompanyIcon, getMediaChannelIcon, BrandLogo } from './components/EnhancedLogoSystem';

const MarketMapApp = () => {
  // Authentication state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [activeTab, setActiveTab] = useState('analysis');

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    product_name: '',
    industry: '',
    geography: '',
    target_user: '',
    demand_driver: '',
    transaction_type: '',
    key_metrics: '',
    benchmarks: '',
    output_format: 'excel'
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedDocData, setUploadedDocData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Debug: Log when uploadedFiles state changes
  useEffect(() => {
    logger.debug('STATE UPDATE - uploadedFiles changed:', uploadedFiles.length, uploadedFiles.map(f => f.name));
  }, [uploadedFiles]);

  useEffect(() => {
    logger.debug('STATE UPDATE - uploadedDocData changed:', uploadedDocData?.documents_processed);
  }, [uploadedDocData]);
  const [analysisProgress, setAnalysisProgress] = useState({
    currentStep: 0,
    totalSteps: 5,
    stepName: '',
    estimatedTimeLeft: 0,
    steps: [
      { name: 'Analyzing Market Landscape with AI', duration: 60 },
      { name: 'Processing Competitive Intelligence', duration: 45 },
      { name: 'Generating Market Segmentation', duration: 30 },
      { name: 'Creating Executive Summary', duration: 50 },
      { name: 'Finalizing Visual Market Map', duration: 15 }
    ]
  });
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');

  // Check if we're in PDF generation mode
  const urlParams = new URLSearchParams(window.location.search);
  const isPDFMode = urlParams.get('pdf') === 'true';
  const analysisIdFromUrl = urlParams.get('analysis_id');

  // Load analysis for PDF mode
  useEffect(() => {
    if (isPDFMode && analysisIdFromUrl) {
      loadAnalysisForPDF(analysisIdFromUrl);
    }
    // loadAnalysisForPDF is a stable async function defined below; safe to omit
    // eslint-disable-next-line
  }, [isPDFMode, analysisIdFromUrl]);

  const loadAnalysisForPDF = async (analysisId) => {
    try {
      const response = await fetch(`${API}/market-maps/${analysisId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
        setCurrentStep(4); // Show results
      }
    } catch (error) {
      logger.error('Error loading analysis for PDF:', error);
    }
  };

  // Check authentication on mount and handle OAuth redirect
  useEffect(() => {
    checkAuth();
    // checkAuth is a stable async function defined below; mount-only effect
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user) {
      loadHistory();
    }
    // loadHistory is a stable async function defined below
    // eslint-disable-next-line
  }, [user]);

  const checkAuth = async () => {
    try {
      // Check if we're coming back from OAuth with session_id in URL FRAGMENT (after #)
      const hashFragment = window.location.hash.substring(1); // Remove the #
      const hashParams = new URLSearchParams(hashFragment);
      const sessionId = hashParams.get('session_id');

      logger.info('Checking auth...');
      logger.info('- Full URL:', window.location.href);
      logger.info('- Hash fragment:', hashFragment);
      logger.info('- Session ID found:', sessionId);

      if (sessionId) {
        logger.info('✅ Found session_id in hash, creating session...');
        // Create session from OAuth redirect
        await createSession(sessionId);
        // Clean URL hash
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // Check existing session
      logger.info('Checking for existing session...');
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      logger.info('Existing session found:', response.data);
      setUser(response.data);
    } catch (error) {
      logger.info('Not authenticated:', error.response?.status);
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  };

  const createSession = async (sessionId) => {
    try {
      logger.info('Creating session with session_id:', sessionId);
      logger.info('API endpoint:', `${API}/auth/session`);
      
      const response = await axios.post(
        `${API}/auth/session`,
        {},
        {
          headers: { 'X-Session-ID': sessionId },
          withCredentials: true
        }
      );
      logger.info('✅ Session created successfully:', response.data);
      setUser(response.data.user);
      setAuthLoading(false);
    } catch (error) {
      logger.error('❌ Session creation failed!');
      logger.error('Error object:', error);
      logger.error('Response status:', error.response?.status);
      logger.error('Response data:', error.response?.data);
      logger.error('Error message:', error.message);
      
      let errorMessage = error.response?.data?.detail || error.message || 'Authentication failed';
      
      // Make error more user-friendly
      if (errorMessage.includes('Invalid session') || errorMessage.includes('expired')) {
        errorMessage = 'Authentication session expired. This can happen if the login process takes too long. Please try logging in again.';
      } else if (errorMessage.includes('not authenticated')) {
        errorMessage = 'Authentication failed. Please try logging in again.';
      }
      
      logger.error('Showing alert:', errorMessage);
      alert(errorMessage);
      
      setAuthLoading(false);
      setUser(null);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setAnalysis(null);
      setHistory([]);
      setCurrentStep(1);
    } catch (error) {
      logger.error('Logout error:', error);
    }
  };

  const loadAnalysis = async (analysisId) => {
    try {
      const response = await axios.get(`${API}/analysis/${analysisId}`, {
        withCredentials: true
      });
      setAnalysis(response.data);
      setCurrentStep(4);
    } catch (error) {
      logger.error('Error loading analysis:', error);
      alert('Error loading analysis. Please try again.');
    }
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/my-analyses`, {
        withCredentials: true
      });
      setHistory(response.data.analyses || []);
    } catch (error) {
      logger.error('Error loading history:', error);
      // Fallback to old endpoint
      try {
        const fallback = await axios.get(`${API}/analysis-history`, {
          withCredentials: true
        });
        setHistory(fallback.data.history || []);
      } catch (e) {
        logger.error('Fallback also failed:', e);
      }
    }
  };

  const updateAnalysis = async (analysisId, updates) => {
    try {
      await axios.put(`${API}/analysis/${analysisId}`, updates, {
        withCredentials: true
      });
      // Refresh history
      loadHistory();
      return true;
    } catch (error) {
      logger.error('Error updating analysis:', error);
      alert(error.response?.data?.detail || 'Error updating analysis');
      return false;
    }
  };

  const toggleSaveAnalysis = async (e, analysisId, currentSaved) => {
    e.stopPropagation();
    const success = await updateAnalysis(analysisId, { is_saved: !currentSaved });
    if (success) {
      // Update local state
      setHistory(prev => prev.map(item => 
        item.id === analysisId ? { ...item, is_saved: !currentSaved } : item
      ));
    }
  };

  const toggleVisibility = async (e, analysisId, currentVisibility) => {
    e.stopPropagation();
    const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
    const success = await updateAnalysis(analysisId, { visibility: newVisibility });
    if (success) {
      setHistory(prev => prev.map(item => 
        item.id === analysisId ? { ...item, visibility: newVisibility } : item
      ));
    }
  };

  const deleteAnalysis = async (e, analysisId, productName) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete the analysis for "${productName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/analysis/${analysisId}`, {
        withCredentials: true
      });
      // Remove from local state
      setHistory(prev => prev.filter(item => item.id !== analysisId));
      // If currently viewing this analysis, go back to form
      if (analysis?.market_map?.id === analysisId) {
        setAnalysis(null);
        setCurrentStep(1);
      }
    } catch (error) {
      logger.error('Error deleting analysis:', error);
      alert(error.response?.data?.detail || 'Error deleting analysis');
    }
  };

  const loadExample = () => {
    setFormData({
      product_name: 'Fitness Tracker',
      industry: 'Wearable Technology',
      geography: 'Global',
      target_user: 'Health-conscious consumers, athletes, casual fitness enthusiasts',
      demand_driver: 'Health and wellness trends, preventive healthcare',
      transaction_type: 'One-time Purchase',
      key_metrics: 'Device sales, subscription revenue, user engagement',
      benchmarks: 'Wearable tech market growing at 9.2% CAGR',
      output_format: 'excel'
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const simulateProgress = () => {
    return new Promise((resolve) => {
      let currentStep = 0;
      const steps = analysisProgress.steps;
      
      const updateProgress = () => {
        if (currentStep < steps.length) {
          const step = steps[currentStep];
          const remainingTime = steps.slice(currentStep + 1).reduce((sum, s) => sum + s.duration, 0);
          
          setAnalysisProgress(prev => ({
            ...prev,
            currentStep: currentStep + 1,
            stepName: step.name,
            estimatedTimeLeft: remainingTime + step.duration
          }));
          
          setTimeout(() => {
            currentStep++;
            updateProgress();
          }, step.duration * 1000);
        } else {
          resolve();
        }
      };
      
      updateProgress();
    });
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    logger.info('🔍 DEBUG - Files from event.target.files:', files.length);
    logger.info('🔍 DEBUG - File names:', files.map(f => f.name));
    logger.info('🔍 DEBUG - File sizes:', files.map(f => f.size));
    
    if (files.length === 0) {
      logger.info('⚠️ No files selected');
      return;
    }

    setIsUploading(true);
    
    // Store files in state IMMEDIATELY before upload
    setUploadedFiles(prevFiles => {
      logger.info('🔍 DEBUG - Previous uploadedFiles state:', prevFiles.length);
      logger.info('🔍 DEBUG - Setting new uploadedFiles state:', files.length);
      return files;
    });
    
    try {
      // Upload files one at a time to avoid body size limits and timeouts
      let allExtractedData = null;
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        logger.info(`Uploading file ${i + 1}/${files.length}:`, file.name, `(${(file.size / 1024 / 1024).toFixed(2)} MB)`);
        
        const singleFormData = new FormData();
        singleFormData.append('files', file);
        
        const response = await axios.post(`${API}/upload-documents`, singleFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          timeout: 180000,  // 3 min timeout per file
          onUploadProgress: (progressEvent) => {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            logger.info(`Upload progress for ${file.name}: ${pct}%`);
          }
        });
        
        logger.info(`File ${i + 1} processed:`, response.data.documents_processed, 'docs');
        
        // Merge extracted data from all files
        if (response.data.data) {
          if (!allExtractedData) {
            allExtractedData = response.data.data;
          } else {
            // Merge arrays
            allExtractedData.market_sizes = [...(allExtractedData.market_sizes || []), ...(response.data.data.market_sizes || [])];
            allExtractedData.growth_rates = [...(allExtractedData.growth_rates || []), ...(response.data.data.growth_rates || [])];
            allExtractedData.competitor_names = [...new Set([...(allExtractedData.competitor_names || []), ...(response.data.data.competitor_names || [])])];
            allExtractedData.key_insights = [...(allExtractedData.key_insights || []), ...(response.data.data.key_insights || [])];
            allExtractedData.total_text = (allExtractedData.total_text || '') + '\n\n' + (response.data.data.total_text || '');
            allExtractedData.documents_processed = (allExtractedData.documents_processed || 0) + (response.data.data.documents_processed || 0);
            allExtractedData.document_summaries = [...(allExtractedData.document_summaries || []), ...(response.data.data.document_summaries || [])];
          }
        }
      }
      
      if (allExtractedData) {
        setUploadedDocData(allExtractedData);
        
        const fileList = allExtractedData.document_summaries?.map((doc, i) => 
          `  ${i + 1}. ${doc.file} (${doc.data_points} data points extracted)`
        ).join('\n') || '';
        
        alert(`${allExtractedData.documents_processed} document(s) processed successfully!\n\n` +
              `Files processed:\n${fileList}\n\n` +
              `Total found:\n` +
              `  - ${allExtractedData.market_sizes?.length || 0} market sizes\n` +
              `  - ${allExtractedData.growth_rates?.length || 0} growth rates\n` +
              `  - ${allExtractedData.competitor_names?.length || 0} competitors`);
      }
      
      // Clear the input so the same files can be selected again if needed
      event.target.value = '';
    } catch (error) {
      logger.error('❌ Error uploading documents:', error);
      logger.error('❌ Error details:', error.response?.data);
      alert(`Error uploading documents: ${error.response?.data?.detail || error.message}\nPlease try again.`);
      // Clear uploaded files on error
      setUploadedFiles([]);
      setUploadedDocData(null);
    } finally {
      setIsUploading(false);
    }
  };

  const removeUploadedFiles = () => {
    setUploadedFiles([]);
    setUploadedDocData(null);
  };

  const analyzeMarket = async () => {
    setIsAnalyzing(true);
    
    // Reset progress
    setAnalysisProgress(prev => ({
      ...prev,
      currentStep: 0,
      stepName: '',
      estimatedTimeLeft: prev.steps.reduce((sum, step) => sum + step.duration, 0)
    }));

    // Start progress simulation (decorative only - don't wait for it)
    simulateProgress();

    try {
      // Include uploaded document data if available
      const analysisData = {
        ...formData,
        uploaded_document_data: uploadedDocData
      };
      
      logger.info('🔍 Analyzing market with data:', {
        product: formData.product_name,
        hasUploadedDocs: !!uploadedDocData,
        docsProcessed: uploadedDocData?.documents_processed,
        marketSizes: uploadedDocData?.market_sizes?.length,
        growthRates: uploadedDocData?.growth_rates?.length,
        competitors: uploadedDocData?.competitor_names?.length
      });

      // Make API call independently. Backend now runs the swarm in a background
      // task and returns a job_id immediately; we poll the job until complete.
      const startResp = await axios.post(`${API}/analyze-market`, analysisData, {
        timeout: 30_000,
        withCredentials: true,
      });
      const jobId = startResp.data?.job_id;
      if (!jobId) {
        throw new Error('Server did not return a job_id.');
      }
      logger.info('Market analysis job started:', jobId);

      // Poll for completion (multi-pass swarm runs ~3-5 min; allow up to 12 min)
      const maxAttempts = 144; // 144 × 5s = 720s = 12 min
      let analysisResult = null;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((res) => setTimeout(res, 5000));
        try {
          const pollResp = await axios.get(`${API}/analyze-market/job/${jobId}`, {
            timeout: 30_000,
            withCredentials: true,
          });
          const status = pollResp.data?.status;
          if (status === 'complete') {
            analysisResult = pollResp.data?.result;
            break;
          }
          if (status === 'failed') {
            throw new Error(pollResp.data?.error || 'Analysis failed.');
          }
          // status === 'running' — keep polling
        } catch (pollErr) {
          if (pollErr.message?.startsWith('Analysis failed')) throw pollErr;
          logger.warn('Poll attempt failed (transient):', pollErr.message);
        }
      }
      if (!analysisResult) {
        throw new Error('Analysis is taking longer than expected. Check History in a moment.');
      }

      logger.info('Market analysis result:', analysisResult);

      // Immediately update state when complete
      setAnalysis(analysisResult);
      setCurrentStep(4);
      loadHistory();
      
      // Stop progress simulation
      setAnalysisProgress(prev => ({
        ...prev,
        currentStep: prev.totalSteps,
        stepName: 'Complete!',
        estimatedTimeLeft: 0
      }));

    } catch (error) {
      logger.error('Error analyzing market:', error);
      
      // More detailed error handling
      if (error.code === 'ECONNABORTED') {
        alert('Analysis timed out. Please try again - the analysis may have completed; check History.');
      } else if (error.response) {
        alert(`Server error: ${error.response.status} - ${error.response.data?.detail || 'Unknown error'}`);
      } else if (error.request) {
        alert('Network error: Unable to connect to the server. Please check your connection.');
      } else {
        alert(error.message || 'Error analyzing market. Please try again.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const exportPersonas = async (analysisId) => {
    try {
      setIsProcessing(true);
      setProcessingMessage('Exporting persona data for Resonate rAI...');
      
      // Use direct navigation to bypass sandbox restrictions
      // The backend returns JSON which will be displayed/downloaded by the browser
      window.location.href = `${BACKEND_URL}/api/export-personas/${analysisId}`;
      
      // Show success message
      setTimeout(() => {
        const successDiv = document.createElement('div');
        successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50';
        const title = document.createElement('div');
        title.className = 'font-bold';
        title.textContent = 'Resonate Export Generated!';
        const subtitle = document.createElement('div');
        subtitle.className = 'text-sm';
        subtitle.textContent = 'JSON data should open in browser';
        successDiv.appendChild(title);
        successDiv.appendChild(subtitle);
        document.body.appendChild(successDiv);
        setTimeout(() => successDiv.remove(), 4000);
      }, 500);
      
    } catch (error) {
      logger.error('Export error:', error);
      alert('Failed to export persona data. Please try again.');
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  };

  const exportMarketMap = async () => {
    try {
      logger.info('Starting Excel export for analysis:', analysis.market_map.id);

      const loadingDiv = document.createElement('div');
      loadingDiv.id = 'download-loading';
      loadingDiv.className = 'fixed top-4 right-4 bg-nordic text-white px-6 py-3 rounded-bcm-md shadow-bcm-raise z-50 font-display font-semibold';
      loadingDiv.textContent = 'Preparing Excel download…';
      document.body.appendChild(loadingDiv);

      const url = `${BACKEND_URL}/api/export-market-map/${analysis.market_map.id}`;

      // Preflight check so we surface server errors instead of a blank tab.
      const probe = await fetch(url, { credentials: 'include' });
      if (!probe.ok) {
        const text = await probe.text();
        document.getElementById('download-loading')?.remove();
        throw new Error(`Server error ${probe.status}: ${text.slice(0, 200)}`);
      }

      // Iframe-sandbox-safe: direct anchor with Content-Disposition handling.
      const filename = `market-map-${analysis.market_input.product_name.replace(/\s+/g, '-').toLowerCase()}.xlsx`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      document.getElementById('download-loading')?.remove();
      logger.info('Excel download triggered for', filename);

      const messageDiv = document.createElement('div');
      messageDiv.className = 'fixed top-4 right-4 bg-billiard text-white px-6 py-4 rounded-bcm-md shadow-bcm-raise z-50 max-w-md font-sans';
      const title = document.createElement('div');
      title.className = 'font-display font-bold mb-1';
      title.textContent = 'Excel Generated';
      const subtitle = document.createElement('div');
      subtitle.className = 'text-sm';
      subtitle.textContent = "Check your Downloads folder (or the new tab) for the file.";
      const file = document.createElement('div');
      file.className = 'text-xs mt-1 opacity-90';
      file.textContent = `File: ${filename}`;
      messageDiv.appendChild(title);
      messageDiv.appendChild(subtitle);
      messageDiv.appendChild(file);
      document.body.appendChild(messageDiv);
      setTimeout(() => messageDiv.remove(), 5000);
    } catch (error) {
      document.getElementById('download-loading')?.remove();
      logger.error('Export failed:', error);
      alert(`Failed to export market map: ${error.message}`);
    }
  };

  const exportPowerPoint = async () => {
    try {
      setIsProcessing(true);
      setProcessingMessage('Generating BCM-branded PowerPoint presentation…');

      const url = `${BACKEND_URL}/api/export-ppt/${analysis.market_map.id}`;
      logger.info('PPT download URL:', url);

      // Preflight to verify the deck generates without errors.
      const probe = await fetch(url, { credentials: 'include' });
      if (!probe.ok) {
        const text = await probe.text();
        throw new Error(`Server error ${probe.status}: ${text.slice(0, 200)}`);
      }

      // Iframe-sandbox-safe: direct anchor click with Content-Disposition.
      const filename = `BCM-Market-Analysis-${analysis.market_input.product_name.replace(/\s+/g, '-')}.pptx`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      logger.info('PPT download triggered for', filename);

      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-billiard text-white px-6 py-4 rounded-bcm-md shadow-bcm-raise z-50 font-sans';
      const title = document.createElement('div');
      title.className = 'font-display font-bold';
      title.textContent = 'PowerPoint Generated';
      const subtitle = document.createElement('div');
      subtitle.className = 'text-sm';
      subtitle.textContent = 'Check your Downloads folder (or the new tab).';
      successDiv.appendChild(title);
      successDiv.appendChild(subtitle);
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 4000);
    } catch (error) {
      logger.error('PowerPoint export error:', error);
      alert('Failed to generate PowerPoint: ' + error.message);
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  };

  const exportPDF = () => {
    try {
      if (!analysis?.market_map?.id) {
        alert('No analysis loaded — cannot export PDF.');
        return;
      }
      logger.info('Starting PDF export for analysis:', analysis.market_map.id);

      // Match the working PowerPoint/Excel pattern: direct navigation to the
      // backend URL. Server returns `Content-Disposition: attachment` so the
      // browser fires its native download dialog WITHOUT actually navigating
      // away. Works inside the Emergent preview iframe AND in production —
      // unlike fetch+blob+createObjectURL (sandboxed) and synthetic
      // `<a target="_blank">` clicks (popup-blocked in some browsers).
      const url = `${BACKEND_URL}/api/export-pdf/${analysis.market_map.id}`;
      window.location.href = url;
      logger.info('PDF download triggered via window.location for', url);
    } catch (error) {
      logger.error('PDF export failed:', error);
      alert(`Failed to export PDF report: ${error.message}`);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  };

  const getCompanyLogo = (companyName) => {
    // Enhanced logo mapping with better fallbacks and direct links
    const logoMap = {
      // Technology Companies
      'Apple': 'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg',
      'Google': 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg',
      'Microsoft': 'https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg',
      'Samsung': 'https://upload.wikimedia.org/wikipedia/commons/2/24/Samsung_Logo.svg',
      'Amazon': 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg',
      'Meta': 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg',
      'IBM': 'https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg',
      'Oracle': 'https://upload.wikimedia.org/wikipedia/commons/5/50/Oracle_logo.svg',
      'Salesforce': 'https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg',
      
      // Financial Services
      'JPMorgan Chase': 'https://upload.wikimedia.org/wikipedia/commons/4/44/JPMorgan_Chase_logo.svg',
      'Goldman Sachs': 'https://upload.wikimedia.org/wikipedia/commons/6/61/Goldman_Sachs.svg',
      'Morgan Stanley': 'https://upload.wikimedia.org/wikipedia/commons/1/12/Morgan_Stanley_Logo_1.svg',
      'Bank of America': 'https://upload.wikimedia.org/wikipedia/commons/4/47/Bank_of_America_logo.svg',
      'Wells Fargo': 'https://upload.wikimedia.org/wikipedia/commons/b/b3/Wells_Fargo_Bank.svg',
      'Citi': 'https://upload.wikimedia.org/wikipedia/commons/1/1c/Citi.svg',
      'DTCC': 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8b/DTCC_logo.svg/320px-DTCC_logo.svg.png',
      'Clearstream': 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%2300529C"%3E%3Cpath d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/%3E%3C/svg%3E',
      'Euroclear': 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%230066CC"%3E%3Cpath d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/%3E%3C/svg%3E',
      
      // Payment Services
      'PayPal': 'https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg',
      'Stripe': 'https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg',
      'Square': 'https://upload.wikimedia.org/wikipedia/commons/6/64/Square%2C_Inc._logo.svg',
      'Visa': 'https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg',
      'Mastercard': 'https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg',
      
      // Fitness & Wearables
      'Fitbit': 'https://upload.wikimedia.org/wikipedia/commons/3/33/Fitbit_logo16.svg',
      'Garmin': 'https://upload.wikimedia.org/wikipedia/commons/5/53/Garmin_logo.svg',
      'Xiaomi': 'https://upload.wikimedia.org/wikipedia/commons/2/29/Xiaomi_logo.svg',
      
      // Food & Beverage
      'Starbucks': 'https://upload.wikimedia.org/wikipedia/en/d/d3/Starbucks_Corporation_Logo_2011.svg',
      'McDonald\'s': 'https://upload.wikimedia.org/wikipedia/commons/3/36/McDonald%27s_Golden_Arches.svg',
      'Coca-Cola': 'https://upload.wikimedia.org/wikipedia/commons/c/ce/Coca-Cola_logo.svg',
      'Sierra Nevada': 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%23228B22"%3E%3Cpath d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/%3E%3C/svg%3E',
      'Stone Brewing': 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%23654321"%3E%3Cpath d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/%3E%3C/svg%3E',
      
      // SaaS/Software
      'Asana': 'https://upload.wikimedia.org/wikipedia/commons/3/3b/Asana_logo.svg',
      'Slack': 'https://upload.wikimedia.org/wikipedia/commons/7/76/Slack_Icon.png',
      'Zoom': 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Zoom_Communications_Logo.svg',
      'HubSpot': 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%23FF7A59"%3E%3Cpath d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/%3E%3C/svg%3E',
      
      // Electric Vehicle/Energy
      'Tesla': 'https://upload.wikimedia.org/wikipedia/commons/b/bb/Tesla_T_symbol.svg',
      'ChargePoint': 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%230099CC"%3E%3Cpath d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/%3E%3C/svg%3E',
      'EVgo': 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%2300B04F"%3E%3Cpath d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/%3E%3C/svg%3E',
      
      // Food Delivery
      'DoorDash': 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%23FF3008"%3E%3Cpath d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/%3E%3C/svg%3E',
      'Uber Eats': 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%23000000"%3E%3Cpath d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/%3E%3C/svg%3E',
      'Grubhub': 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%23F63440"%3E%3Cpath d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/%3E%3C/svg%3E'
    };

    // Return specific logo if mapped, otherwise return a default business icon
    return logoMap[companyName] || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='%23374151'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z'/%3E%3C/svg%3E";
  };

  const resetForm = () => {
    setFormData({
      product_name: '',
      industry: '',
      geography: '',
      target_user: '',
      demand_driver: '',
      transaction_type: '',
      key_metrics: '',
      benchmarks: '',
      output_format: 'excel'
    });
    setAnalysis(null);
    setCurrentStep(1);
  };

  const rerunAnalysis = () => {
    if (!analysis || !analysis.market_input) return;
    const input = analysis.market_input;
    setFormData({
      product_name: input.product_name || '',
      industry: input.industry || '',
      geography: input.geography || '',
      target_user: input.target_user || '',
      demand_driver: input.demand_driver || '',
      transaction_type: input.transaction_type || '',
      key_metrics: input.key_metrics || '',
      benchmarks: input.benchmarks || '',
      output_format: 'excel'
    });
    setAnalysis(null);
    setUploadedFiles([]);
    setUploadedDocData(null);
    setCurrentStep(1);
  };

  const rerunFromHistory = async (e, analysisId) => {
    e.stopPropagation();
    try {
      const response = await axios.get(`${API}/analysis/${analysisId}`, {
        withCredentials: true
      });
      const input = response.data.market_input;
      setFormData({
        product_name: input.product_name || '',
        industry: input.industry || '',
        geography: input.geography || '',
        target_user: input.target_user || '',
        demand_driver: input.demand_driver || '',
        transaction_type: input.transaction_type || '',
        key_metrics: input.key_metrics || '',
        benchmarks: input.benchmarks || '',
        output_format: 'excel'
      });
      setAnalysis(null);
      setUploadedFiles([]);
      setUploadedDocData(null);
      setCurrentStep(1);
    } catch (error) {
      logger.error('Error loading analysis for re-run:', error);
      alert('Error loading analysis. Please try again.');
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <Login />;
  }

  // PDF Mode - render only the analysis results for PDF generation
  if (isPDFMode && analysis) {
    return <PdfModeView analysis={analysis} currentStep={currentStep} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Panel Modal */}
      {showAdminPanel && (
        <AdminPanel user={user} onClose={() => setShowAdminPanel(false)} />
      )}

      {/* Orange Header Stripe */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center mr-4">
                <img 
                  src="https://www.beebyclarkmeyler.com/hs-fs/hubfs/BCM_2024_Logo_Update_White.png?width=2550&height=3300&name=BCM_2024_Logo_Update_White.png" 
                  alt="BCM Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Market Map Generator</h1>
                <p className="text-white text-opacity-90 text-sm">AI-Powered Comprehensive Market Intelligence</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {currentStep === 4 && (
                <button
                  onClick={resetForm}
                  className="bg-white text-orange-600 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  New Analysis
                </button>
              )}
              
              {/* Admin Panel Button */}
              {user.is_admin && (
                <button
                  onClick={() => setShowAdminPanel(true)}
                  className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg font-semibold hover:bg-opacity-30 transition-colors flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <span>Admin Panel</span>
                </button>
              )}
              
              {/* User Menu */}
              <div className="flex items-center space-x-3 bg-white bg-opacity-20 rounded-lg px-4 py-2">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-white text-orange-600 flex items-center justify-center font-semibold">
                    {user.name.charAt(0)}
                  </div>
                )}
                <div className="text-white">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs opacity-90">{user.email}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-2 text-white hover:text-gray-200 transition-colors"
                  title="Logout"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-1" data-testid="main-tabs">
            <button
              onClick={() => setActiveTab('analysis')}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'analysis'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-analysis"
            >
              Market Analysis
            </button>
            <button
              onClick={() => setActiveTab('research')}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'research'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-research"
            >
              Research Library
            </button>
            {(user?.is_admin || user?.agentic_research_enabled) && (
              <button
                onClick={() => setActiveTab('agentic')}
                className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === 'agentic'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid="tab-agentic-research"
              >
                Agentic Research
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      {activeTab === 'agentic' ? (
        <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <AgenticResearch user={user} />
        </main>
      ) : activeTab === 'research' ? (
        <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <ResearchLibrary
            user={user}
            onUseResearch={(researchData) => {
              setActiveTab('analysis');
              setCurrentStep(1);
            }}
          />
        </main>
      ) : (
      <main className="max-w-7xl mx-auto py-8 sm:px-6 lg:px-8">
        {currentStep < 4 && (
            <Wizard
              currentStep={currentStep}
              formData={formData}
              handleInputChange={handleInputChange}
              loadExample={loadExample}
              uploadedFiles={uploadedFiles}
              uploadedDocData={uploadedDocData}
              isUploading={isUploading}
              handleFileUpload={handleFileUpload}
              removeUploadedFiles={removeUploadedFiles}
              isAnalyzing={isAnalyzing}
              setIsAnalyzing={setIsAnalyzing}
              setAnalysisProgress={setAnalysisProgress}
              nextStep={nextStep}
              prevStep={prevStep}
              analyzeMarket={analyzeMarket}
            />
        )}

        {/* Results Step - Market Map */}
        {currentStep === 4 && analysis && (
          <AnalysisResults
            analysis={analysis}
            history={history}
            formatCurrency={formatCurrency}
            updateAnalysis={updateAnalysis}
            toggleSaveAnalysis={toggleSaveAnalysis}
            toggleVisibility={toggleVisibility}
            deleteAnalysis={deleteAnalysis}
            rerunAnalysis={rerunAnalysis}
            rerunFromHistory={rerunFromHistory}
            loadAnalysis={loadAnalysis}
            loadHistory={loadHistory}
            setAnalysis={setAnalysis}
            setCurrentStep={setCurrentStep}
            currentStep={currentStep}
            exportPDF={exportPDF}
            exportPersonas={exportPersonas}
            isProcessing={isProcessing}
            processingMessage={processingMessage}
          />
        )}

        {/* My Market Maps — visible whenever the user isn't viewing a specific analysis */}
        {currentStep !== 4 && (
          <HistoryList
            history={history}
            formatCurrency={formatCurrency}
            loadAnalysis={loadAnalysis}
            toggleSaveAnalysis={toggleSaveAnalysis}
            toggleVisibility={toggleVisibility}
            deleteAnalysis={deleteAnalysis}
            rerunFromHistory={rerunFromHistory}
          />
        )}
      </main>
      )}
    </div>
  );
};

export default MarketMapApp;
